import { useState, type CSSProperties } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { Button, useAuth } from '@org/ui';
import { Camera, IdCard, ShieldCheck, Clock, LogOut } from 'lucide-react';
import { CameraCapture, type CapturedPhoto } from './camera-capture';
import type { PhotoKind } from '../../lib/camera';
import { STORE_PATH } from '../../lib/access';
import { P, btnGhost, btnRed, dmSerif, eyebrowStyle, oswald, PRODUCTS_STYLES } from '../products/theme';
import { useAccess } from './use-access';

/**
 * Full-page identity check for self-signup students: pick selfie or document,
 * take the photo LIVE with the device camera (no uploads — see camera-capture),
 * review it, send. The server answers with the
 * fresh `access.me`: verified → on to the store; rejected with a retry left →
 * try again (either kind); under review / declined → "we are reviewing".
 */
export function IdentityValidation() {
  const { t } = useTranslation('p2e');
  const navigate = useNavigate();
  const { logout } = useAuth();
  const utils = trpc.useUtils();
  const { data } = useAccess();

  const [kind, setKind] = useState<PhotoKind>('selfie');
  const [photo, setPhoto] = useState<CapturedPhoto | null>(null);
  const [outcome, setOutcome] = useState<'idle' | 'approved' | 'rejected'>('idle');

  const submit = trpc.identity.submitPhoto.useMutation({
    onSuccess: (next) => {
      utils.access.me.setData(undefined, next);
      if (next.identity.status === 'verified') {
        setOutcome('approved');
      } else if (next.identity.status === 'unverified') {
        setOutcome('rejected');
        setPhoto(null);
      }
      // needs_review / declined: the status branch below takes over.
    },
  });

  const pickKind = (next: PhotoKind) => {
    if (next === kind) return;
    setKind(next);
    setPhoto(null);
  };

  const status = data?.identity.status ?? 'unverified';
  const attempts = data?.identity.attempts ?? 0;

  let body: React.ReactNode;
  if (outcome === 'approved' || status === 'verified') {
    body = (
      <Outcome
        icon={<ShieldCheck size={40} color={P.green} aria-hidden />}
        title={t('access.validate.approvedTitle')}
        text={t('access.validate.approvedBody')}
        action={
          <Button type="button" variant="red" className="products-btn" style={btnRed} onClick={() => navigate({ to: STORE_PATH })}>
            {t('access.validate.goToStore')}
          </Button>
        }
      />
    );
  } else if (status === 'needs_review' || status === 'declined') {
    body = (
      <Outcome
        icon={<Clock size={40} color={P.gold} aria-hidden />}
        title={t('access.validate.reviewTitle')}
        text={t('access.validate.reviewBody')}
        action={
          <Button type="button" variant="outline" className="products-btn" style={btnGhost} onClick={() => void logout()}>
            <LogOut size={16} aria-hidden /> {t('access.validate.signOut')}
          </Button>
        }
      />
    );
  } else {
    body = (
      <>
        {outcome === 'rejected' && (
          <p role="alert" style={{ ...noteStyle, background: P.goldSoft, color: P.darkText, borderColor: P.gold }}>
            {t('access.validate.rejected', { count: Math.max(0, 2 - attempts), attemptsLeft: Math.max(0, 2 - attempts) })}
          </p>
        )}
        <p style={noteStyle}>{t('access.validate.consent')}</p>

        <div role="radiogroup" aria-label={t('access.validate.kindLabel')} style={{ display: 'flex', gap: 10, margin: '16px 0' }}>
          <KindButton active={kind === 'selfie'} onClick={() => pickKind('selfie')} icon={<Camera size={18} aria-hidden />} label={t('access.validate.kindSelfie')} />
          <KindButton active={kind === 'id'} onClick={() => pickKind('id')} icon={<IdCard size={18} aria-hidden />} label={t('access.validate.kindId')} />
        </div>

        <p style={{ margin: '0 0 6px', fontSize: 14, lineHeight: 1.5, color: P.midText }}>
          {kind === 'selfie' ? t('access.validate.hintSelfie') : t('access.validate.hintId')}
        </p>
        <p style={{ margin: '0 0 12px', fontSize: 12, lineHeight: 1.5, color: P.lightText }}>{t('access.validate.noUpload')}</p>

        {photo ? (
          <div style={{ margin: '0 0 14px' }}>
            <img
              src={photo.previewUrl}
              alt={t('access.validate.previewAlt')}
              style={{ width: '100%', maxHeight: 440, objectFit: 'contain', borderRadius: 14, background: P.blush, border: `1px solid ${P.lightBorder}` }}
            />
            <p style={{ textAlign: 'center', margin: '10px 0 0', fontSize: 14, color: P.midText }}>{t('access.validate.reviewPrompt')}</p>
          </div>
        ) : (
          <div style={{ margin: '0 0 14px' }}>
            <CameraCapture kind={kind} onCapture={setPhoto} disabled={submit.isPending} />
          </div>
        )}

        {submit.isError && (
          <p role="alert" style={{ ...noteStyle, color: P.maxRed }}>
            {t('access.validate.submitError')}
          </p>
        )}

        {photo && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 6 }}>
            <Button type="button" variant="outline" className="products-btn" style={btnGhost} onClick={() => setPhoto(null)} disabled={submit.isPending}>
              {t('access.validate.retake')}
            </Button>
            <Button
              type="button"
              variant="red"
              className="products-btn"
              style={btnRed}
              isLoading={submit.isPending}
              loadingText={t('access.validate.checking')}
              onClick={() => submit.mutate({ kind, imageBase64: photo.imageBase64, mimeType: photo.mimeType })}
            >
              {t('access.validate.send')}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <div style={{ minHeight: '100svh', background: P.cream }}>
      <style dangerouslySetInnerHTML={{ __html: PRODUCTS_STYLES }} />
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '32px 18px 48px' }}>
        <Brand />
        <div style={eyebrowStyle}>{t('access.validate.eyebrow')}</div>
        <h1 style={{ fontFamily: dmSerif, fontWeight: 400, fontSize: 30, lineHeight: 1.1, margin: '4px 0 10px', color: P.darkText }}>
          {t('access.validate.title', { name: data?.firstName ? ` ${data.firstName}` : '' })}
        </h1>
        <p style={{ margin: '0 0 18px', fontSize: 15, lineHeight: 1.55, color: P.darkText }}>{t('access.validate.intro')}</p>
        <section
          style={{
            background: P.white,
            border: `1px solid ${P.lightBorder}`,
            borderRadius: 14,
            boxShadow: `6px 10px 22px ${P.shadow}`,
            padding: 18,
          }}
        >
          {body}
        </section>
      </div>
    </div>
  );
}

const noteStyle: CSSProperties = {
  margin: '0 0 8px',
  padding: '10px 12px',
  borderRadius: 8,
  border: `1px solid ${P.lightBorder}`,
  background: P.blush,
  fontSize: 13,
  lineHeight: 1.5,
  color: P.midText,
};

function KindButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <Button
      type="button"
      role="radio"
      aria-checked={active}
      variant="outline"
      onClick={onClick}
      className={`h-auto flex-1 gap-2 rounded-[10px] border-2 px-2.5 py-3 font-['Oswald'] text-[13px] font-semibold uppercase tracking-[0.08em] ${
        active ? 'border-[#D41920] bg-[#FDF0ED] text-[#D41920] hover:bg-[#FDF0ED]' : 'border-[#F0DAD5] bg-white text-[#2D1A1F]'
      }`}
    >
      {icon}
      {label}
    </Button>
  );
}

function Outcome({ icon, title, text, action }: { icon: React.ReactNode; title: string; text: string; action: React.ReactNode }) {
  return (
    <div style={{ textAlign: 'center', padding: '14px 0 6px' }}>
      <div style={{ marginBottom: 10 }}>{icon}</div>
      <h2 style={{ fontFamily: dmSerif, fontWeight: 400, fontSize: 26, margin: '0 0 8px', color: P.darkText }}>{title}</h2>
      <p style={{ margin: '0 auto 16px', maxWidth: '44ch', fontSize: 15, lineHeight: 1.55, color: P.midText }}>{text}</p>
      <div style={{ display: 'flex', justifyContent: 'center' }}>{action}</div>
    </div>
  );
}

function Brand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 26 }}>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: '#cf2e2e' }}>
        <img src="/icon-hand-small-white.png" alt="" aria-hidden style={{ width: 18, height: 18, objectFit: 'contain' }} />
      </span>
      <span style={{ fontWeight: 600, fontSize: 16, color: P.darkText }}>English2Empower</span>
    </div>
  );
}
